import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { NavigationBar, NavigationConfigs } from '@/components/layout';
import useRestaurantStore from '@/lib/stores/restaurantStore';
import { 
  Upload,
  Image as ImageIcon,
  Eye
} from 'lucide-react';
import { ROUTES } from '@/types';
import ImageMenuUpload from '@/components/menu/ImageMenuUpload';
import ImageMenuGallery from '@/components/menu/ImageMenuGallery';
import { 
  getImageMenuItems, 
  deleteImageMenuItem, 
  reorderImageMenuItems,
  MAX_IMAGE_MENU_ITEMS 
} from '@/lib/firebase/imageMenus';
import type { ImageMenuItem } from '@/types';

/**
 * ImageMenuPage - Manage image menu for restaurants
 * 
 * Features:
 * - Upload multiple images with drag-and-drop
 * - Grid layout with thumbnails
 * - Delete images with confirmation
 * - Reorder images
 * - Camera capture on mobile
 * - Progress tracking for uploads
 */
const ImageMenuPage: React.FC = () => {
  const { id: restaurantId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { restaurants } = useRestaurantStore();
  
  const [images, setImages] = useState<ImageMenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [deletingImageId, setDeletingImageId] = useState<string | null>(null);
  const [reorderingImageIds, setReorderingImageIds] = useState<string[]>([]);

  // Find current restaurant
  const restaurant = restaurants.find(r => r.id === restaurantId);

  const loadImageMenu = useCallback(async () => {
    if (!restaurantId) return;
    
    try {
      setError(null);
      const imageMenuItems = await getImageMenuItems(restaurantId);
      setImages(imageMenuItems);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load images');
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    loadImageMenu();
  }, [loadImageMenu]);

  const handleImageUploaded = (newImage: ImageMenuItem) => {
    setImages(prev => [...prev, newImage]);
  };

  const handlePreviewMenu = () => {
    if (restaurant) {
      const previewUrl = `/menu/${restaurant.id}`;
      window.open(previewUrl, '_blank');
    }
  };

  const handleBack = () => {
    navigate(ROUTES.RESTAURANTS);
  };

  const handleDeleteImage = async (image: ImageMenuItem) => {
    if (!window.confirm(`Delete "${image.fileName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeletingImageId(image.id);
      await deleteImageMenuItem(image.id, image.imageUrl, image.thumbnailUrl);
      setImages(prev => prev.filter(img => img.id !== image.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete image');
    } finally {
      setDeletingImageId(null);
    }
  };

  const handleReorderImages = async (reorderedImages: ImageMenuItem[]) => {
    if (reorderedImages.length === 0) return;
    
    // Find which images were swapped by comparing with current state
    const affectedImageIds: string[] = [];
    
    // Compare positions to find affected images
    for (let i = 0; i < reorderedImages.length; i++) {
      if (images[i]?.id !== reorderedImages[i]?.id) {
        affectedImageIds.push(reorderedImages[i].id);
        // Also add the image that was displaced
        if (images[i]) {
          affectedImageIds.push(images[i].id);
        }
      }
    }
    
    try {
      setReorderingImageIds(affectedImageIds);
      await reorderImageMenuItems(reorderedImages);
      setImages(reorderedImages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reorder images');
    } finally {
      setReorderingImageIds([]);
    }
  };

  const canAddMoreImages = images.length < MAX_IMAGE_MENU_ITEMS;

  if (!restaurant) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <ImageIcon className="w-8 h-8 text-gray-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Restaurant not found</h2>
          <p className="text-gray-600 mb-4">The restaurant you're looking for doesn't exist.</p>
          <Link to={ROUTES.RESTAURANTS}>
            <Button variant="primary">
              Back to Restaurants
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <NavigationBar 
        config={NavigationConfigs.imageMenu(
          restaurant?.name || 'Restaurant',
          handleBack,
          <Button
            variant="ghost"
            size="sm"
            onClick={handlePreviewMenu}
            leftIcon={<Eye className="w-4 h-4" />}
            disabled={images.length === 0}
          >
            Preview
          </Button>
        )} 
      />
      
      {/* Main Content */}
      <main className="max-w-12xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Error Display */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
          >
            <p className="text-red-800 text-sm font-medium">{error}</p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setError(null)}
              className="mt-2"
            >
              Dismiss
            </Button>
          </motion.div>
        )}

        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 sm:mb-8 space-y-4 sm:space-y-0">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Image Menu</h2>
            <p className="text-gray-600 mt-1 sm:mt-2 text-sm sm:text-base">
              Upload photos of your menu for quick setup. Perfect for restaurants who want to show their existing menu.
            </p>
          </div>
          
          {canAddMoreImages && (
            <Button
              variant="primary"
              onClick={() => setShowUpload(true)}
              leftIcon={<Upload className="w-4 h-4" />}
              className="w-full sm:w-auto"
            >
              Upload Images
            </Button>
          )}
        </div>

        {/* Upload Modal */}
        <AnimatePresence>
          {showUpload && canAddMoreImages && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4"
              onClick={() => setShowUpload(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <ImageMenuUpload
                  restaurantId={restaurantId!}
                  currentImageCount={images.length}
                  maxImages={MAX_IMAGE_MENU_ITEMS}
                  onImageUploaded={handleImageUploaded}
                  onUploadComplete={() => setShowUpload(false)}
                  onUploadingChange={() => {}}
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Images Section */}
        <div>
          {/* Images Stats */}
          {(images.length > 0 || loading) && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600 mb-6">
                {loading ? 'Loading images...' : `${images.length}/${MAX_IMAGE_MENU_ITEMS} images uploaded`}
              </p>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="text-center py-8 sm:py-12">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 animate-pulse">
                <ImageIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <p className="text-gray-600 text-sm sm:text-base">Loading your image menu...</p>
            </div>
          )}

          {/* Empty State */}
          {!loading && images.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16"
            >
              <div className="w-24 h-24 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-6">
                <ImageIcon className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                No images uploaded yet
              </h3>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">
                Upload photos of your menu for quick setup. Perfect for restaurants who want to show their existing menu without creating digital items.
              </p>
              <Button
                variant="primary"
                onClick={() => setShowUpload(true)}
                leftIcon={<Upload className="w-5 h-5" />}
              >
                Upload Your First Image
              </Button>
            </motion.div>
          )}

          {/* Image Gallery */}
          {!loading && images.length > 0 && (
            <ImageMenuGallery
              images={images}
              onDeleteImage={handleDeleteImage}
              onReorderImages={handleReorderImages}
              loading={loading}
              deletingImageId={deletingImageId || undefined}
              reorderingImageIds={reorderingImageIds}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default ImageMenuPage;
